import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import AddIcon from "@mui/icons-material/Add";
import BarChartIcon from "@mui/icons-material/BarChart";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BuildIcon from "@mui/icons-material/Build";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckIcon from "@mui/icons-material/Check";
import CloudIcon from "@mui/icons-material/Cloud";
import CodeIcon from "@mui/icons-material/Code";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import StarIcon from "@mui/icons-material/Star";
import {
  Autocomplete,
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  keyframes,
  Paper,
  Popper,
  SvgIconProps,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChurchIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentIcon } from "../../../components/icons";
import {
  AllCrewIcon,
  ConfigurationIcon,
  ContactIcon,
  DashboardIcon,
  EndpointRequestIcon,
  GearIcon,
  MemoryIcon,
  OperationsIcon,
  SettingsIcon,
  UserAdminIcon,
} from "../../sidebar/icons";
import { AppItem, getAllAppRoutes, StoredCustomApp } from "./App";
import { openJobCard } from "../../../utils/openJobCard";

export const CUSTOM_ICON_MAP: Record<
  string,
  React.ComponentType<SvgIconProps>
> = {
  OpenInNew: OpenInNewIcon,
  Bookmark: BookmarkIcon,
  Link: LinkIcon,
  Star: StarIcon,
  Favorite: FavoriteIcon,
  RocketLaunch: RocketLaunchIcon,
  Build: BuildIcon,
  BarChart: BarChartIcon,
  CalendarMonth: CalendarMonthIcon,
  Cloud: CloudIcon,
  Code: CodeIcon,
  AllCrew: AllCrewIcon,
  Configuration: ConfigurationIcon,
  ContactIcon: ContactIcon,
  Dashboard: DashboardIcon,
  EndpointRequest: EndpointRequestIcon,
  Gear: GearIcon,
  Memory: MemoryIcon,
  Operations: OperationsIcon,
  UserAdmin: UserAdminIcon,
  Document: DocumentIcon,
  Settings: SettingsIcon,
  Church: ChurchIcon,
};

const PRESET_COLORS = [
  "#4FD1C5",
  "#63B3ED",
  "#F6AD55",
  "#ED64A6",
  "#48BB78",
  "#9F7AEA",
  "#ED8936",
  "#667EEA",
  "#D53F8C",
  "#FC8181",
  "#4299E1",
  "#10B981",
];


const VISIBILITY_KEY = "app_launcher_visibility";

const shake = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-3deg); }
  75% { transform: rotate(3deg); }
`;


function loadVisibility(): { visible: string[]; hidden: string[] } | null {
  try {
    const raw = localStorage.getItem(VISIBILITY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveVisibility(visible: string[], hidden: string[]) {
  localStorage.setItem(VISIBILITY_KEY, JSON.stringify({ visible, hidden }));
}

function applyVisibility(appList: AppItem[]): {
  visible: AppItem[];
  hidden: AppItem[];
} {
  const saved = loadVisibility();
  if (!saved) {
    return {
      visible: appList,
      hidden: [],
    };
  }

  const byLabel = Object.fromEntries(appList.map((a) => [a.label, a]));
  const visible: AppItem[] = [];
  const hidden: AppItem[] = [];

  saved.visible.forEach((label) => {
    if (byLabel[label]) visible.push(byLabel[label]);
  });
  saved.hidden.forEach((label) => {
    if (byLabel[label]) hidden.push(byLabel[label]);
  });

  const known = new Set([...saved.visible, ...saved.hidden]);
  appList.forEach((a) => {
    if (!known.has(a.label)) visible.push(a);
  });

  return { visible, hidden };
}


interface SortableAppItemProps {
  app: AppItem;
  onAppClick: (app: AppItem) => void;
  isEditMode: boolean;
  onDelete?: (label: string) => void;
}

const SortableAppItem = ({
  app,
  onAppClick,
  isEditMode,
  onDelete,
}: SortableAppItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: app.label });

  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const IconComponent = app.icon;

  const style = {
     transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ClickAwayListener onClickAway={() => setSubmenuAnchor(null)}>
      <Box
        ref={setNodeRef}
        style={style}
        {...(isEditMode ? attributes : {})}
        {...(isEditMode ? listeners : {})}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.75,
          borderRadius: 3,
        }}
      >
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          {isEditMode && app.isCustom && onDelete && (
            <Box
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(app.label);
              }}
              sx={{
                position: "absolute",
                top: -4,
                right: -4,
                zIndex: 10,
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: "error.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 12, color: "white" }} />
            </Box>
          )}
          <Box
            onClick={(e) => {
              if (!isDragging && !isEditMode) {
                e.stopPropagation();
                if (app.children?.length) {
                  setSubmenuAnchor(submenuAnchor ? null : e.currentTarget);
                } else {
                  onAppClick(app);
                }
              }
            }}
            sx={{
              width: 46,
              height: 46,
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: app.color,
              cursor: isEditMode
                ? isDragging
                  ? "grabbing"
                  : "grab"
                : "pointer",
              animation:
                isEditMode && !isDragging
                  ? `${shake} 0.3s ease-in-out infinite`
                  : "none",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: isEditMode ? "none" : `0 6px 16px ${app.color}60`,
              },
            }}
          >
            <IconComponent sx={{ fontSize: 32, color: "white" }} />
          </Box>
        </Box>

        <Typography
          variant="body2"
          fontWeight="500"
          textAlign="center"
          sx={{ fontSize: "0.75rem", lineHeight: 1.1 }}
        >
          {app.label}
        </Typography>

        {app.children?.length && (
          <Popper
            open={Boolean(submenuAnchor)}
            anchorEl={submenuAnchor}
            placement="right-start"
            sx={{ zIndex: 1400 }}
          >
            <Paper
              elevation={6}
              sx={{ py: 0.5, borderRadius: 2, minWidth: 140 }}
            >
              {app.children?.map((child) => (
                <Box
                  key={child.route}
                  onClick={() => {
                    navigate(child.route);
                    setSubmenuAnchor(null);
                  }}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  {child.label}
                </Box>
              ))}
            </Paper>
          </Popper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

type AddAppDialogProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (app: StoredCustomApp) => void;
  existingLabels: string[];
  existingRoutes: string[];
};

const AddAppDialog = ({
  open,
  onClose,
  onAdd,
  existingLabels,
  existingRoutes,
}: AddAppDialogProps) => {
  const [label, setLabel] = useState("");
  const [route, setRoute] = useState("");
  const [selectedIconKey, setSelectedIconKey] = useState("OpenInNew");
  const [selectedColor, setSelectedColor] = useState(PRESET_COLORS[0]);
  const [errors, setErrors] = useState<{ label?: string; route?: string }>({});

  const reset = () => {
    setLabel("");
    setRoute("");
    setSelectedIconKey("OpenInNew");
    setSelectedColor(PRESET_COLORS[0]);
    setErrors({});
  };
  const handleClose = () => {
    reset();
    onClose();
  };

  const validate = () => {
    const newErrors: { label?: string; route?: string } = {};
    if (!label.trim()) newErrors.label = "Name is required";
    else if (existingLabels.includes(label.trim()))
      newErrors.label = "An app with this name already exists";
    if (!route.trim()) newErrors.route = "Route is required";
    else if (!route.startsWith("/"))
      newErrors.route = 'Route must start with "/"';
    else if (existingRoutes.includes(route.trim()))
      newErrors.route = "An app with this route already exists";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = () => {
    if (!validate()) return;
    onAdd({
      label: label.trim(),
      route: route.trim(),
      color: selectedColor,
      iconKey: selectedIconKey,
    });
    reset();
    onClose();
  };

  const PreviewIcon = CUSTOM_ICON_MAP[selectedIconKey];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography fontWeight="600" variant="h6">
          Add Custom App
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Create a shortcut to any route in the app
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.75,
            mb: 3,
            mt: 1,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 3,
              backgroundColor: selectedColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 6px 20px ${selectedColor}60`,
              transition: "all 0.2s ease",
            }}
          >
            <PreviewIcon sx={{ fontSize: 32, color: "white" }} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {label || "Preview"}
          </Typography>
        </Box>
        <TextField
          label="App Name"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          fullWidth
          size="small"
          error={!!errors.label}
          helperText={errors.label}
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 20 }}
        />
        <Autocomplete
          freeSolo
          options={getAllAppRoutes()}
          value={route}
          onInputChange={(_, value) => setRoute(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Route"
              placeholder="/my-custom-page"
              size="small"
              fullWidth
              error={!!errors.route}
              helperText={errors.route || "Type or select an existing route"}
              sx={{ mb: 2.5 }}
            />
          )}
        />
        <Typography variant="caption" color="text.secondary" gutterBottom>
          Icon
        </Typography>
        <Box
          sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5, mt: 0.25 }}
        >
          {Object.entries(CUSTOM_ICON_MAP).map(([key, IconComp]) => (
            <Tooltip key={key} title={key}>
              <Box
                onClick={() => setSelectedIconKey(key)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  border: "2px solid",
                  borderColor:
                    selectedIconKey === key ? "primary.main" : "transparent",
                  backgroundColor:
                    selectedIconKey === key ? "primary.light" : "action.hover",
                  transition: "all 0.15s ease",
                }}
              >
                <IconComp sx={{ fontSize: 20 }} />
              </Box>
            </Tooltip>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          Color
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.25 }}>
          {PRESET_COLORS.map((color) => (
            <Box
              key={color}
              onClick={() => setSelectedColor(color)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: color,
                cursor: "pointer",
                border: "2px solid",
                borderColor:
                  selectedColor === color ? "text.primary" : "transparent",
                outline:
                  selectedColor === color ? `2px solid ${color}` : "none",
                outlineOffset: 2,
                transition: "all 0.15s ease",
              }}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} color="inherit" size="small">
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          size="small"
          disableElevation
        >
          Add App
        </Button>
      </DialogActions>
    </Dialog>
  );
};


type Props = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onOpenToolsDrawer?: () => void;
  onReorder: (apps: AppItem[]) => void;
  onAddCustomApp?: (app: StoredCustomApp) => void;
  onRemoveCustomApp?: (label: string) => void;
  appList: AppItem[];
  placement?:
    | "bottom"
    | "bottom-start"
    | "bottom-end"
    | "top"
    | "top-start"
    | "top-end";
  offset?: [number, number];
};

const DroppableSection = ({
  id,
  children,
  sx,
}: {
  id: string;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <Box ref={setNodeRef} sx={sx}>
      {children}
    </Box>
  );
};

const AppLauncher = ({
  anchorEl,
  open,
  onClose,
  onMouseEnter,
  onMouseLeave,
  onOpenToolsDrawer,
  onReorder,
  onAddCustomApp,
  onRemoveCustomApp,
  appList,
  placement,
  offset,
}: Props) => {
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [draftVisible, setDraftVisible] = useState<AppItem[]>([]);
  const [draftHidden, setDraftHidden] = useState<AppItem[]>([]);

  const draftVisibleRef = useRef<AppItem[]>([]);
  const draftHiddenRef = useRef<AppItem[]>([]);

  const setDraftVisibleSync = (updater: (prev: AppItem[]) => AppItem[]) => {
    setDraftVisible((prev) => {
      const next = updater(prev);
      draftVisibleRef.current = next;
      return next;
    });
  };

  const setDraftHiddenSync = (updater: (prev: AppItem[]) => AppItem[]) => {
    setDraftHidden((prev) => {
      const next = updater(prev);
      draftHiddenRef.current = next;
      return next;
    });
  };

  const [visibleApps, setVisibleApps] = useState<AppItem[]>([]);
  const [hiddenApps, setHiddenApps] = useState<AppItem[]>([]);

  useEffect(() => {
    const { visible, hidden } = applyVisibility(appList);
    setVisibleApps(visible);
    setHiddenApps(hidden);
  }, [appList]);

  useEffect(() => {
    if (!isEditMode) return;
    draftVisibleRef.current = [...visibleApps];
    draftHiddenRef.current = [...hiddenApps];
    setDraftVisible([...visibleApps]);
    setDraftHidden([...hiddenApps]);
  }, [isEditMode, visibleApps, hiddenApps]);

  useEffect(() => {
    if (!open) setIsEditMode(false);
  }, [open]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const findSection = (id: string): "visible" | "hidden" | null => {
    if (draftVisibleRef.current.find((a) => a.label === id)) return "visible";
    if (draftHiddenRef.current.find((a) => a.label === id)) return "hidden";
    return null;
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeLabel = String(active.id);
    const overId = String(over.id);

    const activeSection = findSection(activeLabel);
    const overSection =
      overId === "visible-section"
        ? "visible"
        : overId === "hidden-section"
        ? "hidden"
        : findSection(overId);

    if (!activeSection || !overSection || activeSection === overSection) return;

    if (activeSection === "visible" && overSection === "hidden") {
      const app = draftVisibleRef.current.find((a) => a.label === activeLabel);
      if (!app) return;
      const overIdx = draftHiddenRef.current.findIndex((a) => a.label === overId);

      setDraftVisibleSync((prev) => prev.filter((a) => a.label !== activeLabel));
      setDraftHiddenSync((prev) => {
        const next = [...prev];
        next.splice(overIdx === -1 ? next.length : overIdx, 0, app);
        return next;
      });
    } else if (activeSection === "hidden" && overSection === "visible") {

      const app = draftHiddenRef.current.find((a) => a.label === activeLabel);
      if (!app) return;
      const overIdx = draftVisibleRef.current.findIndex((a) => a.label === overId);

      setDraftHiddenSync((prev) => prev.filter((a) => a.label !== activeLabel));
      setDraftVisibleSync((prev) => {
        const next = [...prev];
        next.splice(overIdx === -1 ? next.length : overIdx, 0, app);
        return next;
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeLabel = String(active.id);
    const overId = String(over.id);

    const activeSection = findSection(activeLabel);
    const overSection =
      overId === "visible-section"
        ? "visible"
        : overId === "hidden-section"
        ? "hidden"
        : findSection(overId);

    if (!activeSection || !overSection) return;

    if (activeSection === overSection) {
      if (overId === "visible-section" || overId === "hidden-section") return;

      if (activeSection === "visible") {
        setDraftVisibleSync((prev) => {
          const oldIdx = prev.findIndex((a) => a.label === activeLabel);
          const newIdx = prev.findIndex((a) => a.label === overId);
          return oldIdx !== -1 && newIdx !== -1
            ? arrayMove(prev, oldIdx, newIdx)
            : prev;
        });
      } else {
        setDraftHiddenSync((prev) => {
          const oldIdx = prev.findIndex((a) => a.label === activeLabel);
          const newIdx = prev.findIndex((a) => a.label === overId);
          return oldIdx !== -1 && newIdx !== -1
            ? arrayMove(prev, oldIdx, newIdx)
            : prev;
        });
      }
    }
  };

  const handleToggleEditMode = () => {
    if (isEditMode) {
      setVisibleApps(draftVisibleRef.current);
      setHiddenApps(draftHiddenRef.current);
      saveVisibility(
        draftVisibleRef.current.map((a) => a.label),
        draftHiddenRef.current.map((a) => a.label),
      );
      onReorder([...draftVisibleRef.current, ...draftHiddenRef.current]);
      setIsEditMode(false);
    } else {
      setIsEditMode(true);
    }
  };

  const handleAppClick = (app: AppItem) => {
    if (app.action === "OPEN_TOOLS_DRAWER") {
      onOpenToolsDrawer?.();
      onClose();
      return;
    }
    if (app.action === "OPEN_JOB_CARD") {
      openJobCard();
      onClose();
      return;
    }
    if (app.route) {
      navigate(app.route);
      onClose();
    }
  };

  const handleDeleteApp = (label: string) => {
    onRemoveCustomApp?.(label);

    setDraftVisibleSync((prev) => prev.filter((app) => app.label !== label));
    setDraftHiddenSync((prev) => prev.filter((app) => app.label !== label));

    setVisibleApps((prev) => prev.filter((app) => app.label !== label));
    setHiddenApps((prev) => prev.filter((app) => app.label !== label));
  };

  const displayVisible = isEditMode ? draftVisible : visibleApps;
  const displayHidden = isEditMode ? draftHidden : hiddenApps;

  return (
    <>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={placement || "bottom"}
        modifiers={[{ name: "offset", options: { offset: offset || [0, 8] } }]}
        sx={{ zIndex: 1300 }}
      >
        <Paper
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          elevation={8}
          sx={{ width: 340, px: 2, py: 2, borderRadius: 4, overflow: "hidden" }}
        >
        
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Typography variant="h6" fontWeight="600">
              Apps
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {!isEditMode && (
                <Tooltip title="Add custom app">
                  <IconButton
                    size="small"
                    onClick={() => setAddDialogOpen(true)}
                  >
                    <AddIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                onClick={handleToggleEditMode}
                size="small"
                sx={{
                  backgroundColor: "transparent",
                  color: "text.primary",
                  "&:hover": { backgroundColor: "action.selected" },
                }}
              >
                {isEditMode ? (
                  <CheckIcon sx={{ fontSize: 22 }} />
                ) : (
                  <EditIcon sx={{ fontSize: 22 }} />
                )}
              </IconButton>
            </Box>
          </Box>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
           
            <SortableContext
              items={displayVisible.map((a) => a.label)}
              strategy={rectSortingStrategy}
            >
              
              <DroppableSection
                id="visible-section"
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 1.5,
                  minHeight: 70,
                }}>
                {displayVisible.map((app) => (
                  <SortableAppItem
                    key={app.label}
                    app={app}
                    onAppClick={handleAppClick}
                    isEditMode={isEditMode}
                    onDelete={app.isCustom ? handleDeleteApp : undefined}
                  />
                ))}
              </DroppableSection>
              {isEditMode && displayVisible.length === 0 && (
                <Box
                  sx={{
                    minHeight: 70,
                    border: "2px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Drag apps here to show them
                  </Typography>
                </Box>
              )}
            </SortableContext>

            <Collapse in={isEditMode}>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
              >
                More Apps
              </Typography>
              <SortableContext
                items={displayHidden.map((a) => a.label)}
                strategy={rectSortingStrategy}
              >
                <DroppableSection
                  id="hidden-section"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 1.5,
                    pt: 0.5,
                    maxHeight: 220,
                    overflowY: "auto",
                    pr: 0.5,
                    minHeight: 70,
                    "&::-webkit-scrollbar": { width: "3px" },
                    "&::-webkit-scrollbar-thumb": {
                      borderRadius: "4px",
                      bgcolor: "divider",
                    },
                  }}
                >
                  {displayHidden.map((app) => (
                    <SortableAppItem
                      key={app.label}
                      app={app}
                      onAppClick={handleAppClick}
                      isEditMode={isEditMode}
                      onDelete={app.isCustom ? handleDeleteApp : undefined}
                    />
                  ))}
                </DroppableSection>
                {displayHidden.length === 0 && (
                  <Box
                    sx={{
                      minHeight: 70,
                      border: "2px dashed",
                      borderColor: "divider",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      All apps are visible
                    </Typography>
                  </Box>
                )}
              </SortableContext>
            </Collapse>
          </DndContext>
        </Paper>
      </Popper>

      <AddAppDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={(stored) => onAddCustomApp?.(stored)}
        existingLabels={appList.map((a) => a.label)}
        existingRoutes={appList
          .map((a) => a.route)
          .filter((r): r is string => !!r)}
      />
    </>
  );
};

export default AppLauncher;
